import {useTranslation} from "react-i18next";
import {useFormContext} from "react-hook-form";
import {Grid, Typography} from "@mui/material";
import {FormAutocomplete, FormDateTimePicker,} from "../../../common/form-fields";
import {FormReportModel} from "../../../api/types/report";
import {useRecoilState} from "recoil";
import {SelectElementModelBE, SelectElementModelFE} from "../../../api/types/generic";
import React, {useContext, useEffect, useState} from "react";
import {fromGenericBeToGenericFe} from "../../../utils";
import {useCommercialSegmentRepository} from "../../../api/repositories/CommercialSegmentRepository";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {COMMERCIAL_SEGMENTS} from "../../../constants";
import {useAcrissRepository} from "../../../api/repositories/AcrissRepository";
import {DealerTenantEntity} from "../../../common/components/DealerAndTenant";

const NumberOfReservationsCarGroupsForm = ({
                                               readOnly,
                                           }: {
    readOnly: boolean;
}) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    //USE FORM CONTEXT
    const {register, watch, setValue} = useFormContext<FormReportModel>();

    const watchAllForm = watch();

    //USE RECOIL ATOM
    const [commercialSegmentList, setCommercialSegmentList] = useRecoilState<
        SelectElementModelFE[]
    >(commercialSegmentListAtom);

    //USE STATE
    const [carGroups, setCarGroups] = useState<string[]>([]);
  const [dealerOptions, setDealerOptions] = useState<SelectElementModelFE[]>([]);
  const [dealerFilteredOptions, setDealerFilteredOptions] = useState<SelectElementModelFE[]>([]);

    // Use Context
    const {
        globalData: {user},
    } = useContext(GlobalContext);

    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name)),
        })
    );

    const optionsFiltered = legalEntityOptions.filter(function (el) {
        return el.code !== "all";
    });

    const updateDealerSelection = async (value: SelectElementModelFE) => {
        setValue(`legalEntity`, value);
        setValue(`dealer`, null);
        const filteredOptionsDealer = user.dealers?.filter(
            (el: DealerTenantEntity) => el.legalEntities?.some((el: { name: string | number; }) => el.name === value?.code)
        );
        const filteredList = filteredOptionsDealer.map(
            (el: DealerTenantEntity) => ({
                code: el.name,
                label: t('translation:dealers.'.concat(el.name)),
            })
        );
        await setDealerOptions(filteredList);
        if (filteredList.length === 1) {
            setValue(`dealer`, filteredList[0]);
        }

        const allCarGroups: string[] = (await getOrderedAcriss(value.code as string)).map(
            (el) => el.acriss
        );
        setCarGroups([t("pricing:actions.selectAll"), ...allCarGroups]);
    };

    //USE HOOK CUSTOM
    const {getCommercialSegments} = useCommercialSegmentRepository();
    const {getOrderedAcriss} = useAcrissRepository();

    const onChangeCarGroups = (input: string[]) => {
        const value: string[] =
            // è stato cliccato "selectAll
            input.find((el: string) => el === t("pricing:actions.selectAll")) &&
            // tra i vecchi valori non c'è "selectAll
            !watchAllForm.numberOfReservationsCarGroups.carGroups?.find(
                (el: string) => el === t("pricing:actions.selectAll")
            )
                ? // applica il "selectAll
                carGroups.filter(
                    (el: string) => el !== t("pricing:actions.selectAll")
                )
                : // setta solo i car group selezionati
                input.filter((el) => el !== t("pricing:actions.selectAll"));
        setValue("numberOfReservationsCarGroups.carGroups", value);
    };

    const handleGetStaticContent = async () => {
        // TODO: togliere COMMERCIAL_SEGMENTS || quando si pusha
        const commercialSegmentsData =
            COMMERCIAL_SEGMENTS || (await getCommercialSegments());
        // const commercialSegmentsData = await getCommercialSegments();

        const commercialSegmentList: SelectElementModelFE[] =
            fromGenericBeToGenericFe(commercialSegmentsData);
        setCommercialSegmentList(commercialSegmentList);
    };

    useEffect(() => {
        handleGetStaticContent();
      if (optionsFiltered.length === 1) {
        updateDealerSelection(optionsFiltered[0]);
      } else {
        let legalEntity = optionsFiltered.find((el) => el.code === watch("legalEntity")?.code);
        let dealer = dealerOptions.find((el) => el.code === watch("dealer")?.code);
        if (legalEntity) {
          updateDealerSelection(legalEntity);
        }
        if (dealer) {
          setValue("dealer", dealer);
        }
      }
    }, []);

  const renderDealerAndTenant = () => {
    return (
      <>
        <Typography variant="h6" width={"100%"} pl={2} mt={4}
                    hidden={optionsFiltered.length <= 1 || dealerFilteredOptions.length <= 1}>
          {t("pricing:alert.labels.dealerAndTenant")}
        </Typography>
        <Typography variant="h6" width={"100%"} pl={2} mt={4} hidden={optionsFiltered.length <= 1 || dealerFilteredOptions.length > 1}>
          {t("pricing:alert.labels.tenant")}
        </Typography>
        <Typography variant="h6" width={"100%"} pl={2} mt={4} hidden={dealerFilteredOptions.length <= 1 || optionsFiltered.length > 1 }>
          {t("pricing:alert.labels.dealer")}
        </Typography>
        <Grid item xs={12} md={3} hidden={optionsFiltered.length <= 1}>
          <FormAutocomplete
            {...register("legalEntity")}
            label={`${t("pricing:dealer.tenant")}*`}
            //getOptionLabel={(option: any) => t('translation:legalEntities.'.concat(option.label))}
            options={optionsFiltered || []}
            handleChange={updateDealerSelection}
            isOptionEqualToValue={(
              option: SelectElementModelFE,
              value: SelectElementModelFE
            ) => option.code === value.code}
          />
        </Grid>
        <Grid item xs={12} md={3} hidden={dealerFilteredOptions.length <= 1}>
          <FormAutocomplete
            {...register(`dealer`)}
            label={`${t("pricing:dealer.dealer")}*`}
            options={dealerOptions || []}
            readOnly={Boolean(!watch("legalEntity"))}
            //getOptionLabel={(option: any) => t('translation:dealers.'.concat(option.label))}
            isOptionEqualToValue={(
              option: SelectElementModelFE,
              value: SelectElementModelFE
            ) => option.code === value.code}
          />
        </Grid>
      </>
    );
  };

    return (
        <Grid container columnSpacing={2}>
            {/* DEALER/TENANT */}
          {renderDealerAndTenant()}
            <Typography variant="h6" width={"100%"} pl={2} mt={4}>
                {t("pricing:report.labels.searchParameters")}
            </Typography>
            {/* PICK UP FROM */}
            <Grid item xs={12} md={3}>
                <FormDateTimePicker
                    {...register("numberOfReservationsCarGroups.startDate")}
                    label={`${t("pricing:report.numberOfDaysOfReservations.startDate")}*`}
                    maxDate={watch("numberOfReservationsCarGroups.endDate") || undefined}
                />
            </Grid>
            {/* PICK UP TO */}
            <Grid item xs={12} md={3}>
                <FormDateTimePicker
                    {...register("numberOfReservationsCarGroups.endDate")}
                    label={`${t("pricing:report.numberOfDaysOfReservations.endDate")}*`}
                    minDate={
                        watch("numberOfReservationsCarGroups.startDate") || undefined
                    }
                    readOnly={!watch("numberOfReservationsCarGroups.startDate")}
                />
            </Grid>
            {/* COMMERCIAL SEGMENT */}
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    {...register("numberOfReservationsCarGroups.smCommercialSegmentId")}
                    label={`${t("pricing:report.form.smCommercialSegment")}`}
                    options={commercialSegmentList}
                    isOptionEqualToValue={(
                        option: SelectElementModelFE,
                        value: SelectElementModelFE
                    ) => option.code === value.code}
                />
            </Grid>
            {/* CAR GROUPS */}
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    multiple={true}
                    {...register("numberOfReservationsCarGroups.carGroups")}
                    label={`${t("pricing:report.form.carGroups")}*`}
                    options={carGroups}
                    handleChange={onChangeCarGroups}
                    isOptionEqualToValue={(option: string, value: string) =>
                        option === value
                    }
                />
            </Grid>
        </Grid>
    );
};

export default NumberOfReservationsCarGroupsForm;
